import { ReactNode, useState, useMemo } from 'react';
import { Link, useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/auth-context';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { organizationService } from '@/services/api/organization.service';
import {
  Menu,
  HelpCircle,
} from 'lucide-react';
import { Sidebar } from '@/components/chrome/Sidebar';
import { BusinessBrand } from '@/components/chrome/BusinessBrand';
import { useFlag } from '@/hooks/useFlags';
import { TopBar } from '@/components/chrome/TopBar';
import { TimezoneIndicator } from '@/components/chrome/TimezoneIndicator';
import { LanguagePill } from '@/components/chrome/LanguagePill';
import {
  MobileTabBar,
  type MobileTabItem,
} from '@/components/chrome/MobileTabBar';
import {
  PageHeaderProvider,
  usePageHeader,
} from '@/components/chrome/PageHeader';
import { IconButton } from '@/components/ui/icon-button';
import { EmailConfirmationBanner } from '@/components/EmailConfirmationBanner';
import { hideBilling } from '@/native/upsell';
import { BillingBanner } from '@/components/BillingBanner';
import { DeletionBanner } from '@/features/accountDeletion/DeletionBanner';
import { ReconnectBanner } from '@/features/quickbooks/ReconnectBanner';
import { homePathForRole, roleLabelKey } from '@/contexts/role-home';
import { EDITOR_ROUTE, QUOTE_TABS, QUOTE_TABS_ESTIMATOR, quoteNavForRole, quoteRouteTitleKey } from '@/features/quotes/nav';
import { ConnectivityBanner } from '@/features/quotes/components/ConnectivityBanner';
import { BRAND_NAME } from '@/config/brand';
import { FeedbackSidebarEntry } from '@/features/feedback/FeedbackSidebarEntry';

interface AdminLayoutProps {
  children: ReactNode;
}

/**
 * Map the current pathname to a TopBar greeting title using the same nav
 * translation keys that label sidebar items. Returns null when no match —
 * the consumer falls back to a default greeting or the page-provided header.
 */
function useRouteGreeting(): { title: string; sub?: string } | null {
  const [location] = useLocation();
  const { t } = useTranslation('common');
  const { t: tq } = useTranslation('quotes');

  return useMemo(() => {
    const key = quoteRouteTitleKey(location);
    if (key) return { title: tq(key) };
    const routes: Array<{ match: (p: string) => boolean; key: string }> = [
      { match: (p) => p.startsWith('/admin/dashboard') || p.startsWith('/staff/dashboard'), key: 'dashboard' },
      { match: (p) => p.startsWith('/admin/team'), key: 'team' },
      { match: (p) => p.startsWith('/admin/billing'), key: 'billing' },
      { match: (p) => p.startsWith('/admin/payments'), key: 'payments' },
      { match: (p) => p.startsWith('/admin/reports'), key: 'reports' },
      { match: (p) => p.startsWith('/admin/organization'), key: 'organization' },
      { match: (p) => p.startsWith('/admin/settings') || p.startsWith('/staff/settings'), key: 'settings' },
      { match: (p) => p.startsWith('/admin/tips'), key: 'helpTips' },
      { match: (p) => p.startsWith('/admin/onboarding'), key: 'onboarding' },
    ];

    const hit = routes.find((r) => r.match(location));
    if (!hit) return null;
    return { title: t(`nav.${hit.key}`, { defaultValue: hit.key }) };
  }, [location, t, tq]);
}

interface AdminTopBarProps {
  organizationTimezone?: string;
  /** The phone's icon-size brand slot (the business logo or the Scopie icon), shown beside the menu button below `lg`. */
  brandMark?: ReactNode;
  onOpenSidebar: () => void;
  /** Sticky positioning is owned by a wrapper (quote-first stacks the connectivity banner above it). */
  sticky?: boolean;
  className?: string;
}

function AdminTopBar({
  organizationTimezone,
  brandMark,
  onOpenSidebar,
  sticky = true,
  className,
}: AdminTopBarProps) {
  const { t } = useTranslation('common');
  const route = useRouteGreeting();
  const pageHeader = usePageHeader();
  const title = pageHeader.title ?? route?.title;

  return (
    <TopBar
      sticky={sticky}
      className={cn('shadow-sm border-b border-line', className)}
      leading={
        <>
          <IconButton
            icon={<Menu />}
            aria-label={t('actions.openMenu', { defaultValue: 'Open menu' })}
            variant="subtle"
            size="md"
            onClick={onOpenSidebar}
            className="lg:hidden"
          />
          {brandMark && <span className="lg:hidden">{brandMark}</span>}
          {title && (
            <TopBar.Greeting
              title={title}
              sub={pageHeader.sub}
            />
          )}
        </>
      }
      trailing={
        <div className="flex items-center gap-2">
          <TimezoneIndicator timezone={organizationTimezone} />
          <LanguagePill />
          <Link href="/admin/tips">
            <IconButton
              icon={<HelpCircle />}
              aria-label={t('nav.helpTips', { defaultValue: 'Help' })}
              variant="subtle"
              size="md"
            />
          </Link>
          {pageHeader.actions && <TopBar.Divider className="hidden md:inline-block" />}
          {pageHeader.actions}
        </div>
      }
    />
  );
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { user, logout } = useAuth();
  const { t } = useTranslation('common');
  const { t: tq } = useTranslation('quotes');
  const { t: tt } = useTranslation('team');
  const [location] = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // GET /organization is refused to estimators (no business settings for them), so they get no organization timezone here.
  const { data: organization, isLoading: organizationLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationService.getOrganization(),
    enabled: user?.role !== 'estimator',
  });

  // only the owner gets the owner navigation (a second line of defence: crew never reach this layout)
  const isAdmin = user?.role === 'admin';
  const homeHref = user?.role === 'staff' || user?.role === 'estimator' ? homePathForRole(user.role) : '/admin/dashboard';

  // Bottom tab bar on mobile (<lg). Four most-used routes per role; the
  // "More" slot reopens the existing drawer-sidebar for everything else.
  // Quote-first (decision 27/33): Quotes, Invoices, Clients, More. The editors draw their own header and
  // footer on a phone, so they hide the top bar and the tab bar there.
  const isEditor = EDITOR_ROUTE.test(location);
  const quoteTabs: MobileTabItem[] = (user?.role === 'estimator' ? QUOTE_TABS_ESTIMATOR : QUOTE_TABS).map((i) => ({ href: i.href, icon: i.icon, label: tq(i.labelKey), matchPrefix: true }));
  const quoteNav = quoteNavForRole(user?.role);
  // Reports is the owner's; each page is behind its own flag (Payments: payments_ledger, Timesheets: timesheets)
  const ledgerOn = useFlag('payments_ledger');
  const timesheetsOn = useFlag('timesheets');
  // Send feedback: on a desktop the owner's Help item moves into the foot menu (Help & Tips > Send feedback); the phone keeps the Help row
  const feedbackOn = useFlag('feedback');
  const helpItem = quoteNav.account.find((i) => i.key === 'help');
  const reportItems = quoteNav.reports.filter((i) => (i.key === 'reportsPayments' ? ledgerOn : i.key === 'reportsTimesheets' ? timesheetsOn : true));

  const mobileTabs: MobileTabItem[] = quoteTabs;

  return (
    <PageHeaderProvider>
      <div className="min-h-[calc(100vh-var(--impersonation-banner-h,0px))] bg-bg-app overflow-x-hidden">
        <Sidebar
          open={sidebarOpen}
          collapsed={sidebarCollapsed}
          onCloseDrawer={() => setSidebarOpen(false)}
          onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
        >
          <Sidebar.Brand
            href={homeHref}
            logoSrc="/brand/scopie-wordmark-white.svg"
            alt={BRAND_NAME}
            mark={<BusinessBrand organization={organization} loading={organizationLoading} variant="wordmark" fallbackSrc="/brand/scopie-wordmark-white.svg" fallbackAlt={BRAND_NAME} />}
          />
              <Sidebar.Nav>
                <Sidebar.Section label={t('nav.workspace')} />
                {quoteNav.workspace.map((item) => (
                  <Sidebar.Item key={item.key} href={item.href} id={`nav-${item.key}`} icon={item.icon} label={tq(item.labelKey)} matchPrefix={item.matchPrefix} />
                ))}
                {quoteNav.manage.length > 0 && (
                  <>
                    <Sidebar.Section label={t('nav.manage')} />
                    {quoteNav.manage.map((item) => (
                      <Sidebar.Item key={item.key} href={item.href} id={`nav-${item.key}`} icon={item.icon} label={tq(item.labelKey)} matchPrefix={item.matchPrefix} />
                    ))}
                  </>
                )}
                {reportItems.length > 0 && (
                  <>
                    <Sidebar.Section label={t('nav.reports')} />
                    {reportItems.map((item) => (
                      <Sidebar.Item key={item.key} href={item.href} id={`nav-${item.key}`} icon={item.icon} label={tq(item.labelKey)} matchPrefix={item.matchPrefix} />
                    ))}
                  </>
                )}
                {/* Account scrolls with the rest, so it is reachable in a short window; only the user block below stays pinned */}
                {quoteNav.account.length > 0 && (
                  <>
                    <Sidebar.Section label={t('nav.account')} />
                    {hideBilling(quoteNav.account).map((item) => (
                      <Sidebar.Item key={item.key} href={item.href} id={`nav-${item.key}`} icon={item.icon} label={tq(item.labelKey)} matchPrefix={item.matchPrefix} className={feedbackOn && item.key === 'help' ? 'lg:hidden' : undefined} />
                    ))}
                  </>
                )}
              </Sidebar.Nav>
          <FeedbackSidebarEntry tipsHref={helpItem?.href} />
          <Sidebar.Foot
            profileHref={isAdmin ? '/admin/settings/profile?tab=myProfile' : '/staff/settings?tab=myProfile'}
            firstName={user?.firstName}
            lastName={user?.lastName}
            role={user?.role ? tt(roleLabelKey(user.role), { defaultValue: user.role }) : undefined}
            onLogout={logout}
            logoutLabel={t('actions.logout')}
          />
        </Sidebar>

        {/* Main content */}
        <div className={cn('transition-all duration-300', sidebarCollapsed ? 'lg:pl-14' : 'lg:pl-60')}>
          <div className={cn('sticky top-0 z-30', isEditor && 'max-lg:static')}>
            {!isEditor && <ConnectivityBanner />}
            <AdminTopBar
              organizationTimezone={organization?.timezone}
              brandMark={<BusinessBrand organization={organization} loading={organizationLoading} variant="icon" fallbackSrc="/brand/scopie-icon.svg" fallbackAlt={BRAND_NAME} />}
              onOpenSidebar={() => setSidebarOpen(true)}
              sticky={false}
              className={isEditor ? 'max-lg:hidden' : undefined}
            />
          </div>

          {/* Page content */}
          <main className={cn('p-4 sm:p-6 lg:p-8 pb-32 sm:pb-8', isEditor && 'max-lg:p-0')}>
            {/* Email Confirmation Banner */}
            <div className={cn('mb-6 empty:hidden', isEditor && 'max-lg:hidden')}>
              <EmailConfirmationBanner />
            </div>
            <div className={cn('mb-6 empty:hidden', isEditor && 'max-lg:hidden')}>
              <DeletionBanner />
            </div>
            <div className={cn('mb-6 empty:hidden', isEditor && 'max-lg:hidden')}>
              <BillingBanner />
            </div>
            <div className={cn('mb-6 empty:hidden', isEditor && 'max-lg:hidden')}>
              <ReconnectBanner />
            </div>

            {children}
          </main>
        </div>

        {!isEditor && <MobileTabBar
          items={[
            ...mobileTabs,
            {
              icon: Menu,
              label: t('actions.more', { defaultValue: 'More' }),
              onClick: () => setSidebarOpen(true),
            },
          ]}
        />}
      </div>
    </PageHeaderProvider>
  );
}
